import { memo, useCallback, useRef, useState } from 'react'
import { Tv } from 'lucide-react'
import { useActiveChannelId, useChannels, useHasPermission } from '../../store/index.js'
import type { CgTemplate } from '../../types/index.js'
import { CgOverlayRulesPanel } from './CgOverlayRulesPanel.js'
import { CgAssetsLibrary } from './CgAssetsLibrary.js'
import { CgFontsLibrary } from './CgFontsLibrary.js'
import { CgTemplateDesigner } from './CgTemplateDesigner.js'
import { CgLogoProfilesPanel } from './CgLogoProfilesPanel.js'
import { PageHeader, PageTabs, type PageTab } from '../common/PageChrome.js'
import './CgStudio.css'

type HabillageTab = 'LOGOS' | 'STUDIO' | 'ASSETS' | 'FONTS' | 'PLAYOUT'

const HABILLAGE_TABS = [
  { id: 'LOGOS', label: 'Logos' },
  { id: 'STUDIO', label: 'Autres habillages' },
  { id: 'ASSETS', label: 'Assets' },
  { id: 'FONTS', label: 'Polices' },
  { id: 'PLAYOUT', label: 'Diffusion' },
] as const satisfies readonly PageTab[]

export const CgOverlaysView = memo(function CgOverlaysView() {
  const leaveGuard = useRef<() => boolean>(() => true)
  const registerLeaveGuard = useCallback((guard: () => boolean) => { leaveGuard.current = guard }, [])
  const channelId = useActiveChannelId()
  const canManage = useHasPermission('CG_MANAGE')
  const channels = useChannels()
  const [tab, setTab] = useState<HabillageTab>('LOGOS')
  const [preferredTemplateId, setPreferredTemplateId] = useState<string | null>(null)
  const [studioTemplateId, setStudioTemplateId] = useState<string | null>(null)
  const [assetsVersion, setAssetsVersion] = useState(0)
  const [fontsVersion, setFontsVersion] = useState(0)
  const [templatesVersion, setTemplatesVersion] = useState(0)
  const [draftAssetIds, setDraftAssetIds] = useState<string[]>([])
  const channel = channels.find(item => item.id === channelId)
  const channelName = channel?.name ?? ''

  const openPlayout = useCallback((template: CgTemplate) => {
    setPreferredTemplateId(template.id)
    setTemplatesVersion(version => version + 1)
    setTab('PLAYOUT')
  }, [])
  const consumePreferredTemplate = useCallback(() => setPreferredTemplateId(null), [])
  // On retient d'ou vient l'ouverture du designer : editer le template d'un
  // logo depuis l'onglet Logos doit y ramener en sortant, pas laisser
  // l'operateur dans Autres habillages.
  const [studioOrigin, setStudioOrigin] = useState<HabillageTab | null>(null)
  // Editer le template d'un logo affiche le designer sans changer d'onglet :
  // l'operateur reste dans Logos, c'est bien un logo qu'il modifie.
  const logoEditorOpen = studioOrigin === 'LOGOS' && studioTemplateId !== null
  const openLogoTemplate = useCallback((templateId: string) => {
    setStudioTemplateId(templateId)
    setTemplatesVersion(version => version + 1)
    setStudioOrigin('LOGOS')
    setTab('LOGOS')
  }, [])
  const exitStudio = useCallback(() => {
    setStudioTemplateId(null)
    setTemplatesVersion(version => version + 1)
    setTab(studioOrigin ?? 'STUDIO')
    setStudioOrigin(null)
  }, [studioOrigin])
  const assetsChanged = useCallback(() => setAssetsVersion(version => version + 1), [])
  const fontsChanged = useCallback(() => setFontsVersion(version => version + 1), [])
  const templatesChanged = useCallback(() => setTemplatesVersion(version => version + 1), [])
  const updateDraftAssetIds = useCallback((assetIds: string[]) => setDraftAssetIds(assetIds), [])
  // Naviguer explicitement par les onglets ferme la session d'edition du logo :
  // sans ca, revenir sur Autres habillages rouvre le logo laisse en plan.
  const handleTabChange = useCallback((nextTab: string) => {
    if (nextTab === tab) return
    if ((tab === 'STUDIO' || logoEditorOpen) && !leaveGuard.current()) return
    setStudioTemplateId(null)
    setStudioOrigin(null)
    setTab(nextTab as HabillageTab)
  }, [tab, logoEditorOpen])

  if (!channelId) return <div className="tp-page-shell cg-page"><PageHeader title="Habillage" /><div className="tp-empty-state">Sélectionnez une chaîne.</div></div>

  return (
    <div className="tp-page-shell cg-page">
      <PageHeader
        title="Habillage"
        actions={(
          <span className="cg-channel-badge" title={`Chaîne active : ${channelName}`}>
            <span className="cg-channel-logo" aria-hidden="true">
              {channel?.logoUrl ? <img src={channel.logoUrl} alt="" /> : <Tv size={14} />}
            </span>
            <span>{channelName}</span>
          </span>
        )}
      />
      <PageTabs items={HABILLAGE_TABS} activeId={tab} onChange={handleTabChange} ariaLabel="Sections habillage" controlsId="cg-panel" idPrefix="cg-tab" />

      <fieldset
        key={channelId}
        id="cg-panel"
        className="cg-page-content catalog-permission-fieldset"
        role="tabpanel"
        aria-labelledby={`cg-tab-${tab}`}
        disabled={!canManage}
        title={!canManage ? 'Consultation seule — permission de gestion des habillages requise' : undefined}
      >
        <div className="cg-tab-panel" hidden={tab !== 'LOGOS' || logoEditorOpen}>
          <CgLogoProfilesPanel
            active={tab === 'LOGOS' && !logoEditorOpen}
            channelId={channelId}
            templatesVersion={templatesVersion}
            onEditTemplate={openLogoTemplate}
            onProfilesChanged={templatesChanged}
          />
        </div>
        <div className="cg-tab-panel" hidden={!logoEditorOpen && tab !== 'STUDIO'}>
          <CgTemplateDesigner active={logoEditorOpen || tab === 'STUDIO'} onLeaveGuard={registerLeaveGuard} channelId={channelId} assetsVersion={assetsVersion} fontsVersion={fontsVersion} templatesVersion={templatesVersion} openTemplateId={studioTemplateId} onExit={studioOrigin ? exitStudio : undefined} hideLogoTemplates onUseTemplate={openPlayout} onAssetUsageChange={updateDraftAssetIds} onAssetsChanged={assetsChanged} onTemplatesChanged={templatesChanged} />
        </div>
        <div className="cg-tab-panel" hidden={tab !== 'ASSETS'}>
          <CgAssetsLibrary active={tab === 'ASSETS'} channelId={channelId} assetsVersion={assetsVersion} protectedAssetIds={draftAssetIds} onAssetsChanged={assetsChanged} />
        </div>
        <div className="cg-tab-panel" hidden={tab !== 'FONTS'}>
          <CgFontsLibrary active={tab === 'FONTS'} fontsVersion={fontsVersion} onFontsChanged={fontsChanged} />
        </div>
        <div className="cg-tab-panel" hidden={tab !== 'PLAYOUT'}>
          <CgOverlayRulesPanel
            active={tab === 'PLAYOUT'}
            channelId={channelId}
            preferredTemplateId={preferredTemplateId}
            templatesVersion={templatesVersion}
            onPreferredTemplateConsumed={consumePreferredTemplate}
          />
        </div>
      </fieldset>
    </div>
  )
})
